import css from "./legal.module.css";

/**
 * Privacy Policy — TV On Guitar LLC.
 *
 * The previous version claimed: *"We do not store personally identifiable
 * information (PII) on our infrastructure."* **That was not true.** The
 * `creators` table stores a `displayName` taken from the Patreon profile, every
 * `files` row is keyed by a person's Auth0 `sub`, and the entitlement cache
 * stores `(sub, creatorId, tierId)`. A pseudonymous identifier tied to an
 * individual is personal data under both GDPR and the CCPA.
 *
 * **Revised 2026-09-08** for `patreon-direct-auth` task 12, which removed
 * Auth0. That changed what is true here in both directions and neither could
 * be left unsaid: the account record Auth0 held — email, sign-in history,
 * approximate location — is gone entirely, and in its place we now store a
 * live Patreon access and refresh token ourselves. Less data about a person,
 * and one more credential. A policy that mentioned only the first half would
 * be flattering rather than accurate.
 *
 * That mattered enough to fix on its own terms. Adam asked for these pages to
 * reduce legal exposure, and an inaccurate privacy representation *creates*
 * exposure — a distinct one from copyright, enforceable by regulators rather
 * than rights holders, and it undermines the credibility of the rest of the
 * document in exactly the place you want to look careful.
 *
 * So this describes what the system actually does. Verified against
 * `CreatorService.ts`, `EntitlementCache.ts`, `AssetKeys.ts`,
 * `DynamoPatreonTokenStore.ts` and `PatreonIdentity.ts`, rather than
 * asserted.
 */

const EFFECTIVE = "September 8, 2026";
const CONTACT = "tvonguitar@gmail.com";
const ENTITY = "TV On Guitar LLC";

export default function PrivacyPolicyPage() {
  return (
    <div className={css.page}>
      <h1 className={css.title}>Privacy Policy</h1>
      <p className={css.meta}>Effective {EFFECTIVE}</p>

      <section className={css.section}>
        <h2 className={css.heading}>1. Introduction</h2>
        <p className={css.body}>
          This service is operated by {ENTITY} (“we”, “our”, “us”). This policy
          explains what we collect, why, and what you can do about it. We have
          tried to describe what the system actually does rather than what is
          convenient to say.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>2. How you sign in</h2>
        <p className={css.body}>
          You sign in with Patreon. We never see or store your Patreon
          password. Patreon asks you to approve access, and then tells us who
          you are and which tiers you support. There is no other sign-in
          provider involved.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>3. What is stored about you</h2>
        <p className={css.body}>
          All of it sits on our own servers. Until September 2026 a sign-in
          provider held a separate account record about you — your email, when
          you last signed in and roughly from where. That record no longer
          exists and we no longer keep anything equivalent.
        </p>
        <ul className={css.list}>
          <li>
            <strong>An account identifier.</strong> Derived from your Patreon
            user id. It is attached to files you upload and to your cached
            membership tier.
          </li>
          <li>
            <strong>Your membership tier</strong>, cached for up to five minutes
            so that every page load does not have to ask Patreon again. It
            records which tier you are on, not your payment details.
          </li>
          <li>
            <strong>A display name</strong>, for accounts that publish content —
            taken from the Patreon profile, and shown as the creator’s name.
          </li>
          <li>
            <strong>Files and their descriptions</strong>, for accounts that
            upload them.
          </li>
          <li>
            <strong>A Patreon access token</strong>, so we can ask Patreon which
            tier you are on without sending you back to sign in on every page.
            It is stored while you are signed in, deleted when you sign out, and
            expires by itself within thirty days if you never do. It is the one
            thing here that would matter if it were stolen, which is why it is
            never sent to your browser and never written to a log.
          </li>
        </ul>
        <p className={css.body}>
          We do <strong>not</strong> store your payment details, card numbers or
          billing address. Those stay with Patreon and we never receive them.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>4. What we read but do not keep</h2>
        <p className={css.body}>
          To work out what you are entitled to see, we read your Patreon
          membership and, for display on your account page, your name, avatar
          and email address. We ask Patreon for these each time they are shown.
          <strong> Your email address is never written to our database.</strong>
          It reaches your screen and goes no further.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>5. Why we hold it</h2>
        <p className={css.body}>
          Solely to run the service: to know who you are, to show you the
          material your membership includes, and to let creators manage what they
          have published. We do not sell personal information, we do not share it
          with advertisers, and we do not use it for advertising or profiling.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>6. Third parties</h2>
        <ul className={css.list}>
          <li>
            <strong>Patreon</strong> — identity and membership. Governed by
            Patreon’s own privacy policy.
          </li>
          <li>
            <strong>Amazon Web Services</strong> — hosting, file storage and
            delivery, in the United States.
          </li>
        </ul>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>7. Cookies and local storage</h2>
        <p className={css.body}>
          We use browser storage to keep you signed in and to remember small
          preferences, such as how you last sorted the library. We do not use
          advertising or third-party tracking cookies, and there is no analytics
          tracking of individual users.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>8. Why we are allowed to hold it</h2>
        <p className={css.body}>
          Where the GDPR applies, our lawful basis is performance of a contract:
          we cannot show you the material your membership includes without
          knowing which membership you have. We do not rely on consent for this,
          and we do not process your data for any purpose beyond running the
          service.
        </p>
        <p className={css.body}>
          Our servers and file storage are in the United States, and your data
          is held there. If you are in the United Kingdom or the European
          Economic Area, that is a transfer to a third country, and it is
          permitted where an approved safeguard is in place. We rely on the
          safeguards Amazon Web Services offers, which include the EU–US Data
          Privacy Framework and its UK extension, and Standard Contractual
          Clauses where those apply.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>9. Retention</h2>
        <p className={css.body}>
          <strong>On our own servers</strong>, if you are a member and do not
          publish content, the only thing stored is one cached record of your
          membership tier. It stops being used after five minutes and is deleted
          automatically; the deletion is performed by our database on its own
          schedule, which can take up to 48 hours. Nothing is written when you
          simply browse or open a file.
        </p>
        <p className={css.body}>
          <strong>Your access token lasts longer than the cache</strong>, but
          not indefinitely: signing out deletes it immediately, and it expires
          on its own within thirty days otherwise. Ending your Patreon
          membership does not delete it — it stops you getting in, which is not
          the same thing — so if you want it gone sooner, sign out or ask us.
        </p>
        <p className={css.body}>
          For accounts that publish content, the display name, files and their
          descriptions are kept while the account exists. Server logs are
          retained for a limited period for debugging and security.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>10. Your rights</h2>
        <p className={css.body}>
          You may ask us what we hold about you, ask for it to be corrected, or
          ask us to delete it, and you may withdraw access at any time by ending
          your Patreon membership. Email {CONTACT} and we will respond. Depending
          on where you live you may also have the right to object to processing,
          to request a copy of your data in a portable form, or to complain to
          your local data protection authority.
        </p>
        <p className={css.body}>
          If you ask us to delete your data we remove everything on our
          servers, including your stored access token. Most of it expires on its
          own — the cached tier within minutes, the token within thirty days —
          so for a member who does not publish content there is usually very
          little left to delete. Patreon holds its own copy of your membership
          and billing information under its own policy; that request has to go
          to Patreon, and we cannot make it for you.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>11. Security</h2>
        <p className={css.body}>
          Files are stored privately and served through short-lived links.
          Access to material is checked on our servers on every request, not in
          your browser. No system is perfectly secure, and we do not claim
          otherwise.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>12. Children</h2>
        <p className={css.body}>
          This service is not directed at children under 13, and we do not
          knowingly collect their personal information.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>13. Changes and contact</h2>
        <p className={css.body}>
          We may update this policy; material changes will be reflected in the
          effective date above. Questions or requests: {ENTITY} — {CONTACT}.
        </p>
      </section>
    </div>
  );
}
